import apiClient from './apiClient';
import { useAuthStore } from './authStore';
const ANALYTICS_STORAGE_KEY = 'scrudix_analytics_events';
const PENDING_ANALYTICS_STORAGE_KEY = 'scrudix_analytics_pending';
const MAX_EVENTS = 250;
const MAX_PENDING_EVENTS = 400;
const MIN_SYNC_INTERVAL_MS = 5000;

type AnalyticsValue = string | number | boolean | null | undefined;
type SyncableAnalyticsValue = string | number | boolean | null;
export type AnalyticsPayload = Record<string, AnalyticsValue>;

function isAnalyticsValue(value: unknown): value is AnalyticsValue {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null ||
    typeof value === 'undefined'
  );
}

export interface AnalyticsEventRecord {
  id: string;
  type: 'page_view' | 'event';
  name: string;
  at: string;
  payload?: AnalyticsPayload;
}

export interface FunnelStepMetric {
  id: string;
  label: string;
  count: number;
  conversion: number;
}

export interface FunnelSnapshot {
  generatedAt: string;
  totalEvents: number;
  steps: FunnelStepMetric[];
}

const isBrowser = typeof window !== 'undefined';
let syncInFlight: Promise<boolean> | null = null;
let lastSyncAt = 0;

const sanitizePayload = (payload: unknown): AnalyticsPayload | undefined => {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }

  const sanitized: AnalyticsPayload = {};
  for (const [key, value] of Object.entries(payload as Record<string, unknown>)) {
    if (isAnalyticsValue(value)) {
      sanitized[key] = value;
    }
  }
  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
};

const normalizeEvent = (raw: unknown): AnalyticsEventRecord | null => {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }

  const candidate = raw as Record<string, unknown>;
  const type = candidate.type;
  const name = candidate.name;
  const at = candidate.at;

  if ((type !== 'page_view' && type !== 'event') || typeof name !== 'string' || typeof at !== 'string') {
    return null;
  }

  return {
    id: typeof candidate.id === 'string' ? candidate.id : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    name,
    at,
    payload: sanitizePayload(candidate.payload),
  };
};

const readCollection = (key: string): AnalyticsEventRecord[] => {
  if (!isBrowser) return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => normalizeEvent(entry))
      .filter((entry): entry is AnalyticsEventRecord => entry !== null);
  } catch {
    return [];
  }
};

const persistCollection = (key: string, events: AnalyticsEventRecord[], max = MAX_EVENTS) => {
  if (!isBrowser) return;
  window.localStorage.setItem(key, JSON.stringify(events.slice(-max)));
};

const readEvents = (): AnalyticsEventRecord[] => readCollection(ANALYTICS_STORAGE_KEY);
const persistEvents = (events: AnalyticsEventRecord[]) => persistCollection(ANALYTICS_STORAGE_KEY, events, MAX_EVENTS);
const readPendingEvents = (): AnalyticsEventRecord[] => readCollection(PENDING_ANALYTICS_STORAGE_KEY);
const persistPendingEvents = (events: AnalyticsEventRecord[]) =>
  persistCollection(PENDING_ANALYTICS_STORAGE_KEY, events, MAX_PENDING_EVENTS);

const hasAuthToken = (): boolean => {
  const token = useAuthStore.getState().token;
  return typeof token === 'string' && token.trim().length > 0;
};

const toSyncPayload = (
  payload?: AnalyticsPayload
): Record<string, SyncableAnalyticsValue> | undefined => {
  if (!payload) return undefined;
  const result: Record<string, SyncableAnalyticsValue> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value === null
    ) {
      result[key] = value;
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const mergeEvents = (incoming: AnalyticsEventRecord[]) => {
  const mergedById = new Map<string, AnalyticsEventRecord>();
  for (const event of readEvents()) {
    mergedById.set(event.id, event);
  }
  for (const event of incoming) {
    mergedById.set(event.id, event);
  }
  const merged = Array.from(mergedById.values()).sort(
    (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()
  );
  persistEvents(merged);
};

const queuePendingEvent = (event: AnalyticsEventRecord) => {
  const current = readPendingEvents();
  current.push(event);
  persistPendingEvents(current);
};

const removePendingEvents = (ids: Set<string>) => {
  if (ids.size === 0) return;
  const remaining = readPendingEvents().filter((event) => !ids.has(event.id));
  persistPendingEvents(remaining);
};

const emit = (type: AnalyticsEventRecord['type'], name: string, payload?: AnalyticsPayload) => {
  if (!isBrowser) return;

  const nextEvent: AnalyticsEventRecord = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    name,
    at: new Date().toISOString(),
    payload: sanitizePayload(payload),
  };

  const current = readEvents();
  current.push(nextEvent);
  persistEvents(current);
  queuePendingEvent(nextEvent);

  if (hasAuthToken()) {
    void syncAnalyticsWithBackend();
  }

  if (import.meta.env.DEV) {
    console.debug('[scrudix analytics]', nextEvent);
  }
};

const shouldStopSyncAttempt = (errorMessage?: string) => {
  if (!errorMessage) return false;
  const message = errorMessage.toLowerCase();
  return message.includes('unauthorized') || message.includes('token');
};

const pushPendingEvents = async () => {
  if (!hasAuthToken()) {
    return;
  }

  const pending = readPendingEvents();
  if (pending.length === 0) {
    return;
  }
  const syncedIds = new Set<string>();

  for (const event of pending) {
    const response = await apiClient.sendAnalyticsEvent({
      type: event.type,
      name: event.name,
      at: event.at,
      clientEventId: event.id,
      payload: toSyncPayload(event.payload),
    });

    if (response.success) {
      syncedIds.add(event.id);
      continue;
    }

    if (shouldStopSyncAttempt(response.error)) {
      break;
    }
  }

  removePendingEvents(syncedIds);
};

const fetchAndMergeRemoteEvents = async () => {
  if (!hasAuthToken()) {
    return;
  }
  const response = await apiClient.getAnalyticsEvents(MAX_EVENTS);
  if (!response.success || !response.data?.events) {
    return;
  }

  const normalizedRemote = response.data.events
    .map((event) => {
      const normalized = normalizeEvent({
        id: event.clientEventId || event.id,
        type: event.type,
        name: event.name,
        at: event.at,
        payload: event.payload,
      });
      return normalized;
    })
    .filter((event): event is AnalyticsEventRecord => event !== null);

  mergeEvents(normalizedRemote);
};

export const trackPageView = (view: string) => {
  emit('page_view', 'view_change', { view });
};

export const trackEvent = (name: string, payload?: AnalyticsPayload) => {
  emit('event', name, payload);
};

export const syncAnalyticsWithBackend = async (force = false): Promise<boolean> => {
  if (!isBrowser || !hasAuthToken()) {
    return false;
  }

  if (!force && Date.now() - lastSyncAt < MIN_SYNC_INTERVAL_MS) {
    return true;
  }

  if (!syncInFlight) {
    syncInFlight = (async () => {
      try {
        await pushPendingEvents();
        await fetchAndMergeRemoteEvents();
        lastSyncAt = Date.now();
        return true;
      } catch {
        return false;
      } finally {
        syncInFlight = null;
      }
    })();
  }

  return syncInFlight;
};

export const getAnalyticsEvents = (limit = 20): AnalyticsEventRecord[] => {
  const normalizedLimit = Number.isFinite(limit)
    ? Math.max(1, Math.min(MAX_EVENTS, Math.floor(limit)))
    : 20;

  return readEvents().slice(-normalizedLimit).reverse();
};

const countEvents = (
  events: AnalyticsEventRecord[],
  predicate: (event: AnalyticsEventRecord) => boolean
) => events.reduce((total, event) => (predicate(event) ? total + 1 : total), 0);

const payloadValue = (event: AnalyticsEventRecord, key: string) => {
  const value = event.payload?.[key];
  return typeof value === 'string' ? value : value == null ? '' : String(value);
};

const toConversion = (current: number, previous: number) => {
  if (previous <= 0) return 0;
  return Math.min(100, Math.round((current / previous) * 100));
};

export const getFunnelSnapshot = (): FunnelSnapshot => {
  const events = readEvents();

  const discoverCount = countEvents(events, (event) => event.type === 'page_view');
  const authIntentCount = countEvents(
    events,
    (event) => event.type === 'event' && (event.name === 'auth_submit' || event.name === 'oauth_start')
  );
  const authSuccessCount = countEvents(
    events,
    (event) => event.type === 'event' && event.name === 'auth_success'
  );
  const integrationCount = countEvents(
    events,
    (event) =>
      event.type === 'event' &&
      (event.name === 'integration_connected' ||
        (event.name === 'onboarding_step_complete' && payloadValue(event, 'step') === 'connect'))
  );
  const activationCount = countEvents(
    events,
    (event) =>
      event.type === 'event' &&
      ((event.name === 'onboarding_step_complete' && payloadValue(event, 'step') === 'command') ||
        event.name === 'first_command_run')
  );

  return {
    generatedAt: new Date().toISOString(),
    totalEvents: events.length,
    steps: [
      {
        id: 'discover',
        label: 'Visited Scrudix',
        count: discoverCount,
        conversion: discoverCount > 0 ? 100 : 0,
      },
      {
        id: 'intent',
        label: 'Started Auth',
        count: authIntentCount,
        conversion: toConversion(authIntentCount, discoverCount),
      },
      {
        id: 'auth',
        label: 'Signed In',
        count: authSuccessCount,
        conversion: toConversion(authSuccessCount, authIntentCount),
      },
      {
        id: 'connect',
        label: 'Connected Tooling',
        count: integrationCount,
        conversion: toConversion(integrationCount, authSuccessCount),
      },
      {
        id: 'activate',
        label: 'Ran First Command',
        count: activationCount,
        conversion: toConversion(activationCount, integrationCount),
      },
    ],
  };
};

export const clearAnalyticsEvents = () => {
  persistEvents([]);
  persistPendingEvents([]);
  if (!hasAuthToken()) {
    return;
  }
  void (async () => {
    try {
      await apiClient.clearAnalyticsEvents();
    } catch {
      // no-op
    }
  })();
};
